import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import posthog from 'posthog-js';
import { cn } from './lib/utils';
import { ThemeProvider } from './components/theme-provider';
import { queryClient } from './lib/query-client';
import { Navbar } from './components/landing/layout/Navbar';
import { DashboardLayout } from './components/dashboard/layout/DashboardLayout';
import { Toaster } from 'sonner';
import { Analytics } from '@vercel/analytics/react';
import { supabase } from './lib/supabase';
import { useSubscription } from './hooks/use-subscription';
import { Session } from '@supabase/supabase-js';
import { CreativeLoadingScreen } from './components/ui/CreativeLoadingScreen';

const LandingPage = lazy(() => import('./pages/LandingPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const AuthPage = lazy(() => import('./pages/AuthPage'));
const IntegrationsPage = lazy(() => import('./pages/IntegrationsPage'));
const AiImplementationPage = lazy(() => import('./pages/AiImplementationPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const SecurityPrivacyPage = lazy(() => import('./pages/SecurityPrivacyPage'));
const DocumentsPage = lazy(() => import('./pages/DocumentsPage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const IntegrationDocsPage = lazy(() => import('./pages/IntegrationDocsPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));

import './App.css';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

// Billing now lives at /profile?tab=billing. Old /billing URLs (Stripe return
// URLs, confirmation emails, bookmarks) redirect there with query params intact.
function BillingRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  params.set('tab', 'billing');
  return <Navigate to={`/profile?${params.toString()}`} replace />;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppContent({ session }: { session: Session | null }) {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isUpdatePassword = searchParams.get('mode') === 'update-password';

  const isAuthPage = location.pathname === '/auth';
  const isDashboard = location.pathname === '/dashboard';
  const isKnowledge = location.pathname === '/knowledge' || location.pathname === '/documents';
  const isIntegrations = location.pathname === '/integrations';
  const isContact = location.pathname === '/contact';
  const isAnalytics = location.pathname === '/analytics';
  const isAiImplementation = location.pathname === '/ai-implementation';
  const isProfile = location.pathname === '/profile';

  const bypassAuth = import.meta.env.DEV && (import.meta.env.VITE_BYPASS_AUTH === 'true' || import.meta.env.VITE_BYPASS_AUTH === true);
  const hasAccess = session || bypassAuth;

  const showNavbar = !isDashboard && !isKnowledge && !isAnalytics && !isProfile && !(isIntegrations && hasAccess) && !(isContact && hasAccess) && !(isAiImplementation && hasAccess);

  // GA4 SPA page_view tracking — fires on every React Router navigation (per D-08, D-09)
  useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }, [location]);

  // Determine which theme to use
  const isDashboardContext = hasAccess && (isDashboard || isKnowledge || isIntegrations || isContact || isAnalytics || isAiImplementation || isProfile);
  const themeClass = isDashboardContext ? 'theme-dashboard' : 'theme-landing';

  return (
    <div className={cn("relative min-h-screen bg-background font-sans antialiased", themeClass)}>
      {showNavbar && <Navbar user={session?.user} minimal={isAuthPage} />}
      <main id="main-content">
        <Suspense fallback={<CreativeLoadingScreen />}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/security-privacy" element={<SecurityPrivacyPage />} />
          <Route
            path="/integrations"
            element={
              hasAccess ? (
                <DashboardLayout>
                  <IntegrationsPage isDashboard />
                </DashboardLayout>
              ) : (
                <IntegrationsPage />
              )
            }
          />
          <Route
            path="/ai-implementation"
            element={
              hasAccess ? (
                <DashboardLayout>
                  <AiImplementationPage isDashboard />
                </DashboardLayout>
              ) : (
                <AiImplementationPage />
              )
            }
          />
          <Route
            path="/contact"
            element={
              hasAccess ? (
                <DashboardLayout>
                  <ContactPage isDashboard />
                </DashboardLayout>
              ) : (
                <ContactPage />
              )
            }
          />
          <Route path="/billing" element={<BillingRedirect />} />
          <Route
            path="/analytics"
            element={
              hasAccess ? (
                <DashboardLayout>
                  <AnalyticsPage isDashboard />
                </DashboardLayout>
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="/profile"
            element={
              hasAccess ? (
                <DashboardLayout>
                  <ProfilePage />
                </DashboardLayout>
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="/auth"
            element={(!hasAccess || isUpdatePassword) ? <AuthPage /> : <Navigate to="/dashboard" replace />}
          />
          <Route
            path="/dashboard"
            element={
              hasAccess ? (
                <DashboardLayout>
                  <DashboardPage />
                </DashboardLayout>
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="/knowledge"
            element={
              hasAccess ? (
                <DashboardLayout>
                  <DocumentsPage />
                </DashboardLayout>
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="/docs/:platformId"
            element={
              hasAccess ? (
                <DashboardLayout>
                  <IntegrationDocsPage />
                </DashboardLayout>
              ) : (
                <IntegrationDocsPage />
              )
            }
          />
          <Route path="/documents" element={<Navigate to="/knowledge" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes >
        </Suspense>
      </main >
    </div >
  );
}

// Keeps the PostHog `plan` super/person property in sync wherever the
// subscription loads (billing, profile, knowledge — not just the dashboard).
// Mounted only for signed-in users: setPersonProperties would upgrade
// anonymous visitors to identified, and the service's no-user fallback
// reports a fake business plan (botLimit > 50 = local-mode fallback,
// same heuristic as BillingPage).
function PlanAnalytics() {
  const { data: subscription } = useSubscription();
  const lastPlanRef = useRef<string | null>(null);

  useEffect(() => {
    if (!subscription || subscription.botLimit > 50) return;
    const plan = subscription.active ? (subscription.planId ?? 'free') : 'free';
    if (lastPlanRef.current === plan) return;
    lastPlanRef.current = plan;
    posthog.register({ plan });
    posthog.setPersonProperties({ plan });
  }, [subscription]);

  return null;
}

function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    }).catch(err => {
      console.warn('Session initialization info:', err.message);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (import.meta.env.DEV) console.log('Auth state change:', event, !!session);
      setSession(session);
      if (session?.user) {
        posthog.identify(session.user.id, {
          email: session.user.email,
          name: session.user.user_metadata?.full_name,
        });
      } else if (event === 'SIGNED_OUT') {
        posthog.reset();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  if (loading) return <CreativeLoadingScreen />;

  return (
    <QueryClientProvider client={queryClient}>
      <Analytics />
      {session && <PlanAnalytics />}
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
        <HelmetProvider>
          <BrowserRouter>
            <ScrollToTop />
            <AppContent session={session} />
          </BrowserRouter>
        </HelmetProvider>
        <Toaster position="top-right" richColors closeButton theme="system" />
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
